import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectLabel, SelectSeparator, SelectGroup } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { priorityColor, statusColor, statusLabel, STATUS_OPTIONS, PAUSED_STATUSES, timeUntil, humanTime, formatDue, formatDuration, formatBizHours, fmtIST } from "@/lib/utils";
import { ArrowLeft, Sparkle, Lock, PaperPlaneRight, Clock, ClockCounterClockwise, WarningOctagon, ChatDots, HardDrives, Paperclip, DownloadSimple, X, GitMerge, CalendarBlank, Star, ArrowFatLinesUp, ShieldCheck, Eye, EnvelopeSimple, WarningCircle, CheckCircle, CaretUpDown, Check, Plus, CaretDown, CaretRight, Pause, ListChecks } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useBreadcrumb } from "@/components/Breadcrumbs";
import { RichText } from "@/components/EmailBody";
import { NewTaskDialog } from "@/pages/Tasks";

const STATUSES = STATUS_OPTIONS.map(o => o.value);
const PRIORITIES = ["P1", "P2", "P3", "P4"];
const TYPES = ["Incident", "Service Request", "Proactive Tasks", "Security Incident"];

// Lazily loads an image attachment's data and renders a clickable inline thumbnail so
// screenshots are viewable directly in the tool (data_url is excluded from the list endpoint).
function AttachmentImage({ att }) {
  const [src, setSrc] = useState(null);
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(false);
  useEffect(() => {
    let on = true;
    api.get(`/attachments/${att.id}`).then(r => { if (on) setSrc(r.data.data_url); }).catch(() => {});
    return () => { on = false; };
  }, [att.id]);
  if (!src) return null;
  return (
    <>
      <button type="button" onClick={() => { setOpen(true); setZoom(false); }} className="block mt-2" data-testid={`attachment-preview-${att.id}`}>
        <img src={src} alt={att.filename} className="max-h-40 w-auto rounded-sm border border-border object-contain hover:opacity-90 transition-opacity cursor-zoom-in" />
      </button>
      {open && (
        <div data-testid={`attachment-lightbox-${att.id}`} onClick={() => setOpen(false)}
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-6 cursor-zoom-out">
          <div className="absolute top-4 left-4 text-white/70 text-xs font-mono max-w-[70vw] truncate">{att.filename}</div>
          <button data-testid={`lightbox-close-${att.id}`} onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            className="absolute top-3 right-3 text-white/80 hover:text-white"><X size={26} /></button>
          <div className="max-h-[92vh] max-w-[94vw] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <img src={src} alt={att.filename} onClick={() => setZoom(z => !z)}
              className={zoom ? "cursor-zoom-out max-w-none w-[200%]" : "cursor-zoom-in max-h-[90vh] max-w-[92vw] object-contain"}
              data-testid={`lightbox-image-${att.id}`} />
          </div>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-[11px] font-mono">Click image to {zoom ? "zoom out" : "zoom in"} · click outside or ✕ to close</div>
        </div>
      )}
    </>
  );
}

export default function IncidentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inc, setInc] = useState(null);
  const [resolveDlg, setResolveDlg] = useState(null);
  useBreadcrumb(inc?.number);
  const [comments, setComments] = useState([]);
  const [users, setUsers] = useState([]);
  const editorRef = useRef(null);
  const [replyEmpty, setReplyEmpty] = useState(true);
  const [stagedFiles, setStagedFiles] = useState([]);
  const [internal, setInternal] = useState(false);
  const [aiOutput, setAiOutput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [canned, setCanned] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [assets, setAssets] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [linkedTasks, setLinkedTasks] = useState([]);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [mergeNum, setMergeNum] = useState("");
  const [statusHistory, setStatusHistory] = useState([]);
  const [groups, setGroups] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [ccEdit, setCcEdit] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  useEffect(() => { setCcEdit(inc?.cc_email || ""); }, [inc?.cc_email]);
  const [bizCal, setBizCal] = useState(null);
  useEffect(() => { api.get("/settings/business-hours").then(r => setBizCal(r.data)).catch(() => {}); }, []);
  const slaPaused = inc?.sla_business_hours && bizCal && !["resolved", "closed", "closed_spam"].includes(inc?.status) && (bizCal.is_holiday_today || bizCal.is_weekend_today);
  const saveCc = () => {
    const parts = ccEdit.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
    const bad = parts.find(p => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(p));
    if (bad) { toast.error(`Invalid CC email: ${bad}`); return; }
    patch({ cc_email: parts.join(", ") });
  };
  const removeCc = (addr) => {
    const remaining = (inc?.cc_email || "").split(/[,;]+/).map(s => s.trim()).filter(Boolean)
      .filter(c => c.toLowerCase() !== addr.toLowerCase());
    patch({ cc_email: remaining.join(", ") });
  };

  const load = useCallback(() => {
    api.get(`/incidents/${id}`).then(r => setInc(r.data));
    api.get(`/incidents/${id}/comments`).then(r => setComments(r.data));
    api.get(`/incidents/${id}/attachments`).then(r => setAttachments(r.data));
    api.get(`/incidents/${id}/duplicates`).then(r => setDuplicates(r.data)).catch(() => {});
    api.get(`/incidents/${id}/status-history`).then(r => setStatusHistory(r.data)).catch(() => {});
    api.get(`/tickets/${id}/tasks`).then(r => setLinkedTasks(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => {
    load();
    api.get('/users').then(r => setUsers(r.data));
    api.get('/assignment-groups').then(r => setGroups(r.data)).catch(() => {});
    api.get('/canned-responses').then(r => setCanned(r.data));
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
    api.get('/clients').then(r => setClients(r.data)).catch(() => {});
  }, [load]);
  useEffect(() => {
    // Assets are scoped to the ticket's client (falls back to all when no client set).
    const c = inc?.client;
    const url = c ? `/assets?client=${encodeURIComponent(c)}` : '/assets';
    api.get(url).then(r => setAssets(r.data)).catch(() => setAssets([]));
  }, [inc?.client]);

  if (!inc) return <div className="p-6 text-muted-foreground">Loading ticket…</div>;

  const patch = async (updates) => {
    try {
      const { data } = await api.patch(`/incidents/${id}`, updates);
      setInc(data);
      if (updates.status) api.get(`/incidents/${id}/status-history`).then(r => setStatusHistory(r.data)).catch(() => {});
      toast.success("Ticket updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  // Assignee, Client and Category are mandatory before any status change (all ticket types).
  const mandatoryMissing = [
    !inc?.assignee_id && "Assignee",
    !(inc?.client || "").trim() && "Client",
    !(inc?.category || "").trim() && "Category",
  ].filter(Boolean);
  const changeStatus = (v) => {
    if (!inc || v === inc.status) return;
    // Closed-Spam bypasses all guards: no mandatory fields, no resolution summary.
    if (v === "closed_spam") {
      patch({ status: v });
      return;
    }
    if (mandatoryMissing.length) {
      toast.error(`Set ${mandatoryMissing.join(", ")} before changing status`);
      return;
    }
    if (v === "resolved" || v === "closed") {
      setResolveDlg({ status: v, note: inc.resolution_note || "" });
      return;
    }
    patch({ status: v });
  };
  const confirmResolve = () => {
    const note = (resolveDlg?.note || "").trim();
    if (note.length < 10) { toast.error("Add a resolution summary (at least 10 characters)"); return; }
    patch({ status: resolveDlg.status, resolution_note: note });
    setResolveDlg(null);
  };

  const ATT_ACCEPT = ".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  const DOC_TYPES = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
  const attAllowed = (type, name = "") => (type || "").startsWith("image/") || DOC_TYPES.includes(type) || /\.(png|jpe?g|gif|webp|bmp|pdf|docx?)$/i.test(name);
  const escHtml = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const syncEmpty = () => {
    const el = editorRef.current;
    setReplyEmpty(!el || (!(el.innerText || "").trim() && !el.querySelector("img")));
  };
  const readEditor = () => {
    const el = editorRef.current;
    if (!el) return { body: "", isHtml: false };
    if (el.querySelector("img")) return { body: el.innerHTML, isHtml: true };
    return { body: (el.innerText || "").trim(), isHtml: false };
  };
  const insertAtCursor = (html) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    document.execCommand("insertHTML", false, html);
    syncEmpty();
  };
  const onEditorPaste = (e) => {
    const items = e.clipboardData?.items || [];
    if (!internal) {
      for (const it of items) {
        if (it.type && it.type.startsWith("image/")) {
          e.preventDefault();
          const file = it.getAsFile();
          if (file.size > 5_000_000) { toast.error("Image too large (5MB max)"); return; }
          const reader = new FileReader();
          reader.onload = () => insertAtCursor(`<img src="${reader.result}" alt="pasted image" />`);
          reader.readAsDataURL(file);
          return;
        }
      }
    }
    e.preventDefault();
    document.execCommand("insertText", false, e.clipboardData.getData("text/plain"));
    syncEmpty();
  };
  const onAttachSelect = (e) => {
    Array.from(e.target.files || []).forEach((file) => {
      if (!attAllowed(file.type, file.name)) { toast.error(`${file.name}: only images, PDF or Word`); return; }
      if (file.size > 5_000_000) { toast.error(`${file.name}: too large (5MB max)`); return; }
      const reader = new FileReader();
      reader.onload = () => setStagedFiles((prev) => [...prev, {
        id: Math.random().toString(36).slice(2), name: file.name,
        type: file.type || "application/octet-stream", size: file.size,
        dataUrl: reader.result, isImage: (file.type || "").startsWith("image/"),
      }]);
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };
  const removeStaged = (fid) => setStagedFiles((prev) => prev.filter((f) => f.id !== fid));

  const submitReply = async () => {
    const { body } = readEditor();
    if (!body.trim() && stagedFiles.length === 0) return;
    const attachments = (!internal ? stagedFiles : []).map((f) => ({
      filename: f.name, content_type: f.type, size: f.size, data_url: f.dataUrl,
    }));
    await api.post(`/incidents/${id}/comments`, { body, internal, attachments });
    if (editorRef.current) editorRef.current.innerHTML = "";
    setReplyEmpty(true); setStagedFiles([]); setInternal(false); setPreview(null);
    load();
  };

  const openPreview = async () => {
    const { body } = readEditor();
    if (!body.trim()) { toast.info("Type a reply to preview the email"); return; }
    setPreviewLoading(true);
    try {
      const { data } = await api.post(`/incidents/${id}/reply-preview`, { body, internal });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build preview");
    } finally { setPreviewLoading(false); }
  };

  const sendFromPreview = async () => {
    await submitReply();
    toast.success("Reply posted");
  };

  const runAI = async (mode) => {
    setAiLoading(true); setAiOutput("");
    try {
      const token = localStorage.getItem("helix_token");
      const res = await fetch(`${API}/ai/assist`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ incident_id: id, mode }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        setAiOutput(prev => prev + decoder.decode(value));
      }
    } catch (e) {
      setAiOutput("[AI error: " + e.message + "]");
    } finally { setAiLoading(false); }
  };

  const sla = timeUntil(inc.sla_resolution_due_at);
  const respSla = timeUntil(inc.sla_response_due_at);
  const closed = inc.status === 'resolved' || inc.status === 'closed' || inc.status === 'closed_spam';
  const linkedAssetIds = inc.linked_asset_ids || [];
  const linkedAssets = assets.filter(a => linkedAssetIds.includes(a.id));

  const toggleAsset = async (assetId) => {
    const next = linkedAssetIds.includes(assetId)
      ? linkedAssetIds.filter(x => x !== assetId)
      : [...linkedAssetIds, assetId];
    const { data } = await api.patch(`/incidents/${id}`, { linked_asset_ids: next });
    setInc(data);
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5_000_000) { toast.error("File too large (5MB max)"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/incidents/${id}/attachments`, {
          filename: file.name, content_type: file.type || "application/octet-stream",
          size: file.size, data_url: reader.result,
        });
        toast.success("Attachment uploaded");
        api.get(`/incidents/${id}/attachments`).then(r => setAttachments(r.data));
      } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
      finally { setUploading(false); e.target.value = ""; }
    };
    reader.readAsDataURL(file);
  };

  const downloadAttachment = async (att) => {
    const { data } = await api.get(`/attachments/${att.id}`);
    const a = document.createElement("a");
    a.href = data.data_url; a.download = data.filename; a.click();
  };

  const mergeInto = async (targetId, number) => {
    if (!window.confirm(`Merge ${inc.number} INTO ${number}? This ticket will be closed and its comments moved.`)) return;
    try {
      await api.post(`/incidents/${id}/merge`, { target_id: targetId });
      toast.success(`Merged into ${number}`);
      navigate(`/incidents/${targetId}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Merge failed"); }
  };

  const mergeByNumber = async () => {
    const num = (mergeNum || "").trim().toUpperCase();
    if (!num) return;
    if (num === (inc.number || "").toUpperCase()) { toast.error("Cannot merge a ticket into itself"); return; }
    if (!window.confirm(`Merge ${inc.number} INTO ${num}? This ticket will be closed and its comments moved.`)) return;
    try {
      const { data } = await api.post(`/incidents/${id}/merge`, { target_number: num });
      toast.success(`Merged into ${num}`);
      setMergeNum("");
      navigate(`/incidents/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Merge failed"); }
  };

  const escalate = async () => {
    if (!window.confirm(`Escalate ${inc.number}? This bumps priority, logs a note, and alerts Teams.`)) return;
    try {
      const { data } = await api.post(`/incidents/${id}/escalate`);
      setInc(data);
      load();
      toast.success(`Escalated to ${data.priority}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Escalation failed"); }
  };

  const insertCanned = (cid) => {
    const tpl = canned.find(c => c.id === cid);
    if (!tpl) return;
    const filled = tpl.body
      .replaceAll("{{customer}}", inc.customer_name || inc.requester_email.split("@")[0])
      .replaceAll("{{ticket_number}}", inc.number)
      .replaceAll("{{agent_name}}", "Agent");
    const html = filled.split("\n").map(escHtml).join("<br>");
    const el = editorRef.current;
    insertAtCursor(el && (el.innerText || "").trim() ? "<br><br>" + html : html);
  };

  const suggestCanned = async () => {
    try {
      const { data } = await api.post("/ai/suggest-canned", { incident_id: id });
      if (data.template_id) {
        insertCanned(data.template_id);
        toast.success(`AI picked: ${data.title}`);
      } else {
        toast.info("No templates available");
      }
    } catch (e) { toast.error("AI suggest failed"); }
  };

  return (
    <div className="p-6" data-testid="incident-detail-view">
      <Link to="/incidents" data-testid="back-to-queue" className="text-xs font-mono text-muted-foreground hover:text-primary flex items-center gap-1 mb-3">
        <ArrowLeft size={14} /> Back to queue
      </Link>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className="font-mono text-sm text-primary">{inc.number}</span>
            <span className={`text-[10px] font-mono px-2 py-0.5 border ${priorityColor(inc.priority)}`}>{inc.priority}</span>
            <span className={`text-[10px] font-mono px-2 py-0.5 border uppercase ${statusColor(inc.status)}`}>{inc.status.replace('_',' ')}</span>
            {inc.merged_into && <span className="text-[10px] font-mono px-2 py-0.5 border border-border text-muted-foreground flex items-center gap-1"><GitMerge size={11} /> MERGED</span>}
            {inc.alert_occurrences > 1 && <span data-testid="alert-occurrences-badge" className="text-[10px] font-mono px-2 py-0.5 border border-rag-amber/40 bg-rag-amber-bg text-rag-amber-fg flex items-center gap-1" title={`This alert has recurred ${inc.alert_occurrences} times${inc.alert_key ? ` (key: ${inc.alert_key})` : ""}`}><WarningOctagon size={11} weight="bold" /> ×{inc.alert_occurrences} OCCURRENCES</span>}
            {inc.requester_responded && !closed && <span data-testid="requester-responded-badge" className="text-[10px] font-mono px-2 py-0.5 border border-rag-red/40 text-rag-red-fg flex items-center gap-1"><ChatDots size={11} weight="bold" /> REQUESTOR RESPONDED</span>}
            {inc.escalated && <span data-testid="escalated-badge" className="text-[10px] font-mono px-2 py-0.5 border border-rag-amber/50 text-rag-amber-fg flex items-center gap-1"><ArrowFatLinesUp size={11} /> ESCALATED</span>}
            {!closed && sla.breach && (
              <span className="text-[10px] font-mono px-2 py-0.5 bg-rag-red text-background border border-rag-red flex items-center gap-1 pulse-breach">
                <WarningOctagon size={12} /> SLA BREACH
              </span>
            )}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">{inc.title}</h1>
          <div className="text-xs font-mono text-muted-foreground mt-1">
            Requested by {inc.requester_email} · via {inc.source} · <span data-testid="inc-created-ist">{fmtIST(inc.created_at)}</span>
          </div>
        </div>
        {closed && (
          <Button
            data-testid="copy-csat-link"
            variant="outline"
            size="sm"
            onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/feedback/${id}`); toast.success("CSAT survey link copied"); }}
            className="rounded-sm text-[10px] font-mono uppercase border-primary/40 shrink-0"
          ><Star size={12} weight="fill" className="mr-1.5 text-primary" /> Copy survey link</Button>
        )}
        {!closed && (
          <Button
            data-testid="escalate-btn"
            variant="outline"
            size="sm"
            onClick={escalate}
            className="rounded-sm text-[10px] font-mono uppercase border-rag-amber/50 text-rag-amber-fg shrink-0"
          ><ArrowFatLinesUp size={12} className="mr-1.5" /> Escalate</Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left 2/3 - conversation */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-6 border-border rounded-sm">
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Description</div>
            <RichText text={inc.description} className="text-sm leading-relaxed" />
          </Card>

          {!closed && (
            <Card className="border-rag-amber/40 rounded-sm bg-rag-amber-bg" data-testid="duplicates-card">
              <div className="p-4 border-b border-rag-amber/30 flex items-center gap-2">
                <GitMerge size={14} className="text-rag-amber-fg" />
                <span className="text-[11px] font-mono uppercase tracking-widest text-rag-amber-fg">Potential Duplicates{duplicates.length > 0 ? ` (${duplicates.length})` : ""}</span>
              </div>
              <div className="p-4 space-y-2">
                {duplicates.map(d => (
                  <div key={d.id} data-testid={`duplicate-${d.number}`} className="flex items-center gap-3 text-sm">
                    <Link to={`/incidents/${d.id}`} className="font-mono text-primary hover:underline">{d.number}</Link>
                    <span className="truncate flex-1">{d.title}</span>
                    <span className="text-[10px] font-mono text-muted-foreground">{Math.round(d.score*100)}% match</span>
                    <Button data-testid={`merge-into-${d.number}`} onClick={()=>mergeInto(d.id, d.number)} size="sm" variant="outline" className="h-7 rounded-sm text-[10px] font-mono uppercase border-rag-amber/40">
                      <GitMerge size={11} className="mr-1" /> Merge into
                    </Button>
                  </div>
                ))}
                {duplicates.length === 0 && (
                  <div className="text-xs text-muted-foreground">No likely duplicates detected. You can still merge into a specific ticket below.</div>
                )}
                <div className="pt-3 mt-1 border-t border-rag-amber/20">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Merge into another ticket</div>
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="manual-merge-number"
                      value={mergeNum}
                      onChange={(e)=>setMergeNum(e.target.value)}
                      onKeyDown={(e)=>{ if (e.key === "Enter") mergeByNumber(); }}
                      placeholder="Ticket number e.g. INC-01234"
                      className="rounded-sm h-8 text-xs font-mono"
                    />
                    <Button data-testid="manual-merge-btn" onClick={mergeByNumber} disabled={!mergeNum.trim()} size="sm" variant="outline" className="h-8 rounded-sm text-[10px] font-mono uppercase border-rag-amber/40 shrink-0">
                      <GitMerge size={11} className="mr-1" /> Merge
                    </Button>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1.5">This ticket ({inc.number}) will be closed and its comments moved into the target.</div>
                </div>
              </div>
            </Card>
          )}

          {Array.isArray(inc.alert_occurrence_log) && inc.alert_occurrence_log.length > 0 && (
            <Card className="border-border rounded-sm" data-testid="occurrence-timeline">
              <div className="p-4 border-b border-border flex items-center gap-2">
                <WarningOctagon size={13} weight="bold" className="text-rag-amber-fg" />
                <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Alert occurrences · {inc.alert_occurrence_log.length}{inc.alert_key ? ` · key ${inc.alert_key}` : ""}</div>
              </div>
              <div className="p-4 max-h-[220px] overflow-y-auto">
                <ol className="relative border-l border-border ml-1.5 space-y-3">
                  {[...inc.alert_occurrence_log].reverse().map((o, idx) => (
                    <li key={idx} className="ml-4" data-testid={`occurrence-item-${idx}`}>
                      <span className={`absolute -left-[5px] mt-1 h-2 w-2 rounded-full ${o.kind === "clear" ? "bg-rag-green" : "bg-rag-amber"}`} />
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded-sm border ${o.kind === "clear" ? "bg-rag-green-bg text-rag-green-fg border-rag-green/40" : "bg-rag-amber-bg text-rag-amber-fg border-rag-amber/40"}`}>{o.kind === "clear" ? "Cleared" : "Fired"}</span>
                        <span className="text-xs font-mono text-muted-foreground" title={o.at}>{formatDue(o.at)}</span>
                        {o.from && <span className="text-[11px] text-muted-foreground truncate">from {o.from}</span>}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </Card>
          )}


          <Card className="border-border rounded-sm">
            <div className="p-4 border-b border-border">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Timeline · {comments.length} entries</div>
            </div>
            <div className="p-4 space-y-4 max-h-[400px] overflow-y-auto">
              {comments.length === 0 && <div className="text-sm text-muted-foreground">No comments yet.</div>}
              {comments.map(c => (
                <div key={c.id} className={`p-3 rounded-sm border ${c.internal ? 'bg-rag-amber-bg border-rag-amber/30' : 'bg-accent/20 border-border'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium">{c.author_name}</span>
                    {c.internal && <span className="text-[10px] font-mono uppercase tracking-widest text-rag-amber-fg flex items-center gap-1"><Lock size={10} /> internal</span>}
                    <span className="text-[10px] font-mono text-muted-foreground ml-auto" title={fmtIST(c.created_at, { seconds: true })}>{fmtIST(c.created_at)}</span>
                  </div>
                  <div className="text-sm"><RichText text={c.body} /></div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-border space-y-3">
              <div className="flex items-center gap-2">
                <ChatDots size={14} className="text-primary" />
                <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Insert template</span>
                <Select onValueChange={insertCanned}>
                  <SelectTrigger data-testid="canned-picker" className="w-56 h-8 rounded-sm text-xs"><SelectValue placeholder="Choose a canned response…" /></SelectTrigger>
                  <SelectContent>
                    {canned.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button data-testid="ai-suggest-canned" onClick={suggestCanned} size="sm" variant="outline" className="h-8 rounded-sm text-[10px] font-mono uppercase border-primary/40">
                  <Sparkle size={12} weight="fill" className="mr-1 text-primary" /> AI suggest
                </Button>
              </div>
              <div className="relative">
                <div
                  ref={editorRef}
                  data-testid="reply-editor"
                  contentEditable
                  suppressContentEditableWarning
                  onInput={syncEmpty}
                  onPaste={onEditorPaste}
                  className="reply-editor rounded-sm min-h-[100px] max-h-[380px] overflow-auto border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring whitespace-pre-wrap [&_img]:max-w-[280px] [&_img]:my-1 [&_img]:rounded-sm [&_img]:border [&_img]:border-border"
                />
                {replyEmpty && (
                  <div className="pointer-events-none absolute left-3 top-2 text-sm text-muted-foreground">
                    {internal ? "Type an internal note…" : "Type a reply… (paste a screenshot to embed it inline)"}
                  </div>
                )}
              </div>
              {!internal && stagedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="reply-staged-files">
                  {stagedFiles.map(f => (
                    <div key={f.id} data-testid={`reply-staged-${f.id}`} className="flex items-center gap-2 bg-accent/30 border border-border rounded-sm px-2 py-1 text-xs">
                      {f.isImage ? <img src={f.dataUrl} alt={f.name} className="h-8 w-8 object-cover rounded-sm border border-border" /> : <Paperclip size={13} className="text-primary" />}
                      <span className="max-w-[160px] truncate font-mono">{f.name}</span>
                      <button type="button" data-testid={`reply-staged-remove-${f.id}`} onClick={()=>removeStaged(f.id)} className="text-muted-foreground hover:text-rag-red-fg"><X size={13} /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Switch data-testid="internal-toggle" checked={internal} onCheckedChange={(v)=>{ setInternal(v); if(v) setStagedFiles([]); }} />
                  <Label className="text-xs font-mono uppercase text-muted-foreground">Internal note</Label>
                </div>
                {!internal && (
                  <label data-testid="reply-attach-label" className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-muted-foreground hover:text-primary cursor-pointer border border-dashed border-border hover:border-primary rounded-sm px-2 py-1.5 transition-colors">
                    <input data-testid="reply-attach-input" type="file" multiple accept={ATT_ACCEPT} onChange={onAttachSelect} className="hidden" />
                    <Paperclip size={13} /> Attach files
                  </label>
                )}
                <Button data-testid="preview-email-btn" onClick={openPreview} disabled={previewLoading || replyEmpty} variant="outline" className="ml-auto rounded-sm font-mono text-xs uppercase tracking-widest border-primary/40">
                  <Eye size={14} className="mr-2" />{previewLoading ? "Building…" : "Preview email"}
                </Button>
                <Button data-testid="send-reply-btn" onClick={submitReply} disabled={replyEmpty && stagedFiles.length===0} className="rounded-sm font-mono text-xs uppercase tracking-widest">
                  <PaperPlaneRight size={14} className="mr-2" />Post
                </Button>
              </div>
            </div>
          </Card>

          <Card className="border-primary/40 rounded-sm bg-primary/5">
            <div className="p-4 border-b border-primary/30 flex items-center gap-2">
              <Sparkle size={14} className="text-primary" weight="fill" />
              <span className="text-[11px] font-mono uppercase tracking-widest text-primary">TRAK AI Copilot</span>
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" data-testid="ai-summarize" onClick={()=>runAI('summarize')} disabled={aiLoading} className="rounded-sm text-xs h-8 border-primary/40">Summarize</Button>
                <Button size="sm" variant="outline" data-testid="ai-suggest" onClick={()=>runAI('suggest_reply')} disabled={aiLoading} className="rounded-sm text-xs h-8 border-primary/40">Suggest reply</Button>
                <Button size="sm" variant="outline" data-testid="ai-categorize" onClick={()=>runAI('categorize')} disabled={aiLoading} className="rounded-sm text-xs h-8 border-primary/40">Categorize</Button>
              </div>
            </div>
            <div className="p-4 text-sm whitespace-pre-wrap min-h-[80px] font-mono text-zinc-700 dark:text-zinc-200">
              {aiLoading && !aiOutput && <span className="text-muted-foreground">Thinking…</span>}
              {aiOutput || (!aiLoading && <span className="text-muted-foreground">Choose an action above to run TRAK AI on this ticket.</span>)}
            </div>
          </Card>
        </div>

        {/* Right 1/3 - metadata */}
        <div className="space-y-4">
          <Card className="p-4 border-border rounded-sm">
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
              SLA Timers
              {inc.sla_business_hours && <span className="text-[9px] px-1.5 py-0.5 border border-primary/40 text-primary flex items-center gap-1"><CalendarBlank size={10} /> BUSINESS HRS</span>}
              {slaPaused && (
                <span data-testid="sla-paused-badge" title={bizCal.is_holiday_today ? `Today (${bizCal.today}) is a calendar holiday${bizCal.holiday_name_today ? ` — ${bizCal.holiday_name_today}` : ""}. SLA clocks resume on the next business day.` : "Today is outside business days. SLA clocks resume on the next business day."}
                  className="text-[9px] px-1.5 py-0.5 border border-rag-amber-fg/60 bg-rag-amber-bg text-rag-amber-fg flex items-center gap-1 ml-auto">
                  <Pause size={10} weight="fill" /> PAUSED · {bizCal.is_holiday_today ? `HOLIDAY${bizCal.holiday_name_today ? ` · ${bizCal.holiday_name_today.toUpperCase()}` : ""}` : "WEEKEND"}
                </span>
              )}
            </div>
            {slaPaused && (
              <div data-testid="sla-paused-note" className="text-[11px] text-rag-amber-fg/90 font-mono mb-3 -mt-1">
                Timers are frozen today — the remaining time shown will not change until the next business day.
              </div>
            )}
            <div className="space-y-3">
              <div>
                <div className="text-[10px] font-mono uppercase text-muted-foreground">Response {inc.sla_response_breached ? '· breached' : inc.responded_at ? '· met' : (respSla.breach ? '· overdue' : 'due by')}</div>
                <div className={`font-mono text-base ${inc.sla_response_breached ? 'text-rag-red-fg' : inc.responded_at ? 'text-rag-green-fg' : respSla.breach ? 'text-rag-red-fg' : 'text-rag-amber-fg'} flex items-center gap-1`} data-testid="sla-response-due">
                  <Clock size={13} /> {inc.sla_response_breached ? 'Breached' : inc.responded_at ? 'Met' : formatDue(inc.sla_response_due_at)}
                </div>
                {!inc.responded_at && !inc.sla_response_breached && <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{(() => { const useBiz = inc.sla_response_biz_left_seconds != null; const t = useBiz ? formatBizHours(inc.sla_response_biz_left_seconds) : respSla.text; const biz = useBiz ? ' biz hrs' : ''; return respSla.breach ? `overdue by ${t.replace('-', '')}${biz}` : `${t}${biz} remaining`; })()}</div>}
                {inc.responded_at && <div className="text-[10px] font-mono text-muted-foreground mt-0.5" data-testid="sla-response-achieved">achieved in {formatDuration(Math.max(0, (new Date(inc.responded_at).getTime() - new Date(inc.created_at).getTime()) / 1000))}</div>}
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase text-muted-foreground">Resolution {inc.sla_resolution_breached ? '· breached' : closed ? '· met' : PAUSED_STATUSES.includes(inc.status) ? '· paused' : sla.breach ? '· overdue' : 'due by'}</div>
                <div className={`font-mono text-base ${inc.sla_resolution_breached ? 'text-rag-red-fg' : closed ? 'text-rag-green-fg' : PAUSED_STATUSES.includes(inc.status) ? 'text-muted-foreground' : sla.breach ? 'text-rag-red-fg' : 'text-rag-amber-fg'} flex items-center gap-1`} data-testid="sla-resolution-due">
                  <Clock size={13} /> {inc.sla_resolution_breached ? 'Breached' : closed ? 'Met' : formatDue(inc.sla_resolution_due_at)}
                </div>
                {!closed && !inc.sla_resolution_breached && <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{PAUSED_STATUSES.includes(inc.status) ? 'timer paused' : (() => { const useBiz = inc.sla_biz_left_seconds != null; const t = useBiz ? formatBizHours(inc.sla_biz_left_seconds) : sla.text; const biz = useBiz ? ' biz hrs' : ''; return sla.breach ? `overdue by ${t.replace('-', '')}${biz}` : `${t}${biz} remaining`; })()}</div>}
                {inc.resolved_at && inc.status !== 'closed_spam' && <div className="text-[10px] font-mono text-muted-foreground mt-0.5" data-testid="sla-resolution-achieved">achieved in {formatDuration(Math.max(0, (new Date(inc.resolved_at).getTime() - new Date(inc.created_at).getTime()) / 1000 - (inc.sla_hold_ms || 0) / 1000))} <span className="opacity-60">· working time</span></div>}
              </div>
              <div className="pt-3 border-t border-border/60 flex flex-wrap gap-1.5" data-testid="sla-governance">
                <span className="text-[10px] font-mono px-1.5 py-0.5 border border-border rounded-sm text-muted-foreground flex items-center gap-1" title="SLA plan governing this ticket">
                  <ShieldCheck size={10} className="text-primary" /> {inc.sla_plan_name || 'Default SLA'}
                </span>
                <span className="text-[10px] font-mono px-1.5 py-0.5 border border-border rounded-sm text-muted-foreground flex items-center gap-1" title="Business-hours calendar">
                  <CalendarBlank size={10} className="text-primary" /> {inc.sla_business_hours ? (inc.business_hours_name || 'Default hours') : '24×7'}
                </span>
              </div>
            </div>
          </Card>

          <Card className="p-4 border-border rounded-sm" data-testid="status-timeline-card">
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
              <ClockCounterClockwise size={13} className="text-primary" /> Status Timeline
            </div>
            {statusHistory.length === 0 ? (
              <div className="text-xs text-muted-foreground">No status changes recorded.</div>
            ) : (
              <ol className="relative border-l border-border ml-1.5 space-y-4">
                {statusHistory.map((h, i) => (
                  <li key={h.id} data-testid={`status-history-${i}`} className="ml-4">
                    <span className={`absolute -left-[5px] w-2.5 h-2.5 rounded-full ${i === statusHistory.length - 1 ? 'bg-primary' : 'bg-muted-foreground/50'}`} />
                    <div className="text-sm flex items-center gap-1.5 flex-wrap">
                      {h.from_status
                        ? <><span className="font-mono text-[11px] px-1.5 py-0.5 border rounded-sm uppercase text-muted-foreground">{h.from_status.replace('_',' ')}</span>
                            <span className="text-muted-foreground">→</span>
                            <span className={`font-mono text-[11px] px-1.5 py-0.5 border rounded-sm uppercase ${statusColor(h.to_status)}`}>{h.to_status.replace('_',' ')}</span></>
                        : <span className={`font-mono text-[11px] px-1.5 py-0.5 border rounded-sm uppercase ${statusColor(h.to_status)}`}>{h.to_status.replace('_',' ')}</span>}
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground mt-1">
                      {fmtIST(h.created_at)}{h.actor_name ? ` · ${h.actor_name}` : ''}{h.note ? ` · ${h.note}` : ''}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Card>

          <Card className="p-4 border-border rounded-sm space-y-3">
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Attributes</div>
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Type</Label>
              <Select value={inc.type || "Incident"} onValueChange={(v)=>patch({type: v})}>
                <SelectTrigger data-testid="edit-type" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Status</Label>
              <Select value={inc.status} onValueChange={changeStatus}>
                <SelectTrigger data-testid="edit-status" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              {mandatoryMissing.length > 0 && (
                <div data-testid="status-mandatory-note" className="text-[10px] font-mono text-rag-amber-fg mt-1">
                  Set {mandatoryMissing.join(", ")} to change status
                </div>
              )}
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Priority</Label>
              <Select value={inc.priority} onValueChange={(v)=>patch({priority: v})}>
                <SelectTrigger data-testid="edit-priority" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Assignee / Group {!inc.assignee_id && <span className="text-red-500" title="Required to change status">*</span>}</Label>
              <Select value={inc.assignee_id || "unassigned"} onValueChange={(v)=>{
                if (v === 'unassigned') patch({assignee_id: null});
                else if (v.startsWith('grp:')) patch({group_id: v.slice(4)});
                else patch({assignee_id: v});
              }}>
                <SelectTrigger data-testid="edit-assignee" className="mt-1 rounded-sm"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {groups.length > 0 && (
                    <>
                      <SelectSeparator />
                      <SelectGroup>
                        <SelectLabel className="text-[10px] font-mono uppercase text-muted-foreground">Assignment Groups</SelectLabel>
                        {groups.map(g => <SelectItem key={g.id} value={`grp:${g.id}`} data-testid={`assign-group-${g.id}`}>{g.name} <span className="text-muted-foreground">· group</span></SelectItem>)}
                      </SelectGroup>
                    </>
                  )}
                  <SelectSeparator />
                  <SelectGroup>
                    <SelectLabel className="text-[10px] font-mono uppercase text-muted-foreground">Agents</SelectLabel>
                    {users.filter(u => u.role !== 'end_user').map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {inc.group && <div className="text-[10px] font-mono text-muted-foreground mt-1" data-testid="ticket-group">Group: <span className="text-primary">{inc.group}</span></div>}
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Category {!(inc.category || "").trim() && <span className="text-red-500" title="Required to change status">*</span>}</Label>
              <Select value={inc.category || ""} onValueChange={(v)=>patch({category: v})}>
                <SelectTrigger data-testid="edit-category" className="mt-1 rounded-sm"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {(categories.includes(inc.category) ? categories : [inc.category, ...categories].filter(Boolean)).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Client {!(inc.client || "").trim() && <span className="text-red-500" title="Required to change status">*</span>}</Label>
              <Popover open={clientOpen} onOpenChange={(o)=>{ setClientOpen(o); if (!o) setClientSearch(""); }}>
                <PopoverTrigger asChild>
                  <button data-testid="edit-client" role="combobox" aria-expanded={clientOpen}
                    className="mt-1 flex w-full items-center justify-between gap-2 rounded-sm border border-input bg-background px-3 h-10 text-sm hover:bg-accent/40 transition-colors">
                    <span className={`truncate ${inc.client ? "" : "text-muted-foreground"}`}>{inc.client || "No client"}</span>
                    <CaretUpDown size={13} className="opacity-60 shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[--radix-popover-trigger-width] rounded-sm" align="start">
                  <Command>
                    <CommandInput data-testid="edit-client-search" placeholder="Search or type a client…" value={clientSearch} onValueChange={setClientSearch} className="text-sm" />
                    <CommandList>
                      <CommandEmpty>
                        {clientSearch.trim()
                          ? <div className="px-2 py-1.5 text-xs text-muted-foreground">Press the "Add" option below to use this name.</div>
                          : "No clients found."}
                      </CommandEmpty>
                      <CommandGroup>
                        <CommandItem value="__none" data-testid="edit-client-none" onSelect={()=>{ patch({ client: "" }); setClientOpen(false); setClientSearch(""); }}>
                          <Check size={13} className={`mr-2 ${!inc.client ? "opacity-100 text-primary" : "opacity-0"}`} />
                          <span className="text-sm text-muted-foreground">No client</span>
                        </CommandItem>
                        {clients.map(c => (
                          <CommandItem key={c} value={c} data-testid={`edit-client-opt-${c}`} onSelect={()=>{ patch({ client: c }); setClientOpen(false); setClientSearch(""); }}>
                            <Check size={13} className={`mr-2 ${inc.client === c ? "opacity-100 text-primary" : "opacity-0"}`} />
                            <span className="text-sm">{c}</span>
                          </CommandItem>
                        ))}
                        {clientSearch.trim() && !clients.some(c => c.toLowerCase() === clientSearch.trim().toLowerCase()) && (
                          <CommandItem value={clientSearch} data-testid="edit-client-add-new" onSelect={()=>{ patch({ client: clientSearch.trim() }); setClientOpen(false); setClientSearch(""); }}>
                            <Plus size={13} className="mr-2 text-primary" />
                            <span className="text-sm">Add "{clientSearch.trim()}"</span>
                          </CommandItem>
                        )}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </Card>

          {/* Contacts */}
          <Card className="p-4 border-border rounded-sm space-y-3" data-testid="contacts-card">
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2"><ChatDots size={13} className="text-primary" /> Contacts</div>
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Requester</Label>
              <div className="text-sm mt-1 font-mono break-all">{inc.requester_email}</div>
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">CC (captured from email · kept in the loop on updates)</Label>
              {(inc.cc_email || "").trim() && (
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="cc-chips">
                  {inc.cc_email.split(/[,;]+/).map(s => s.trim()).filter(Boolean).map(c => (
                    <span key={c} data-testid={`cc-chip-${c}`} className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 border border-border rounded-sm bg-muted/40">
                      {c}
                      <button data-testid={`cc-remove-${c}`} onClick={() => removeCc(c)} title="Remove from CC" className="text-muted-foreground hover:text-red-500 transition-colors"><X size={11} weight="bold" /></button>
                    </span>
                  ))}
                </div>
              )}
              <Textarea data-testid="edit-cc-email" value={ccEdit} onChange={(e)=>setCcEdit(e.target.value)} placeholder="colleague@company.com, manager@company.com" className="mt-2 rounded-sm text-sm min-h-[38px] font-mono" />
              <Button data-testid="save-cc-email" size="sm" variant="outline" disabled={ccEdit === (inc.cc_email || "")} onClick={saveCc} className="mt-2 h-7 rounded-sm text-[10px] font-mono uppercase">Save CC</Button>
            </div>
          </Card>

          {/* Linked Assets / CI */}
          <Card className="p-4 border-border rounded-sm" data-testid="linked-assets-card">
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
              <HardDrives size={13} className="text-primary" /> Configuration Items
            </div>
            {linkedAssets.length > 0 ? (
              <div className="space-y-2 mb-3">
                {linkedAssets.map(a => (
                  <div key={a.id} data-testid={`linked-asset-${a.asset_tag}`} className="flex items-center gap-2 text-xs bg-accent/20 border border-border rounded-sm px-2 py-1.5">
                    <span className="font-mono text-primary">{a.asset_tag}</span>
                    <button data-testid={`unlink-asset-${a.asset_tag}`} onClick={()=>toggleAsset(a.id)} className="ml-auto text-muted-foreground hover:text-red-700 dark:hover:text-red-400"><X size={12} /></button>
                  </div>
                ))}
              </div>
            ) : <div className="text-xs text-muted-foreground mb-3">No assets linked.</div>}
            {!inc.client ? (
              <div data-testid="asset-client-required" className="text-[11px] text-rag-amber-fg border border-rag-amber/40 bg-rag-amber-bg rounded-sm px-2 py-2">
                Select a client for this ticket (in Attributes) before linking assets.
              </div>
            ) : (
              <Select value="" onValueChange={(v)=>toggleAsset(v)}>
                <SelectTrigger data-testid="link-asset-select" className="rounded-sm h-8 text-xs"><SelectValue placeholder={`Link an asset (${inc.client})…`} /></SelectTrigger>
                <SelectContent>
                  {assets.filter(a => !linkedAssetIds.includes(a.id)).map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.asset_tag}</SelectItem>
                  ))}
                  {assets.filter(a => !linkedAssetIds.includes(a.id)).length === 0 && (
                    <div className="px-2 py-2 text-[11px] text-muted-foreground">No assets available for {inc.client}.</div>
                  )}
                </SelectContent>
              </Select>
            )}
          </Card>

          {/* Linked Tasks (Task Management module) */}
          <Card className="p-4 border-border rounded-sm" data-testid="linked-tasks-card">
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
              <ListChecks size={13} className="text-primary" /> Tasks
              <span className="ml-auto font-mono normal-case tracking-normal">{linkedTasks.filter(t => t.status !== "Done").length} open</span>
            </div>
            {linkedTasks.length > 0 ? (
              <div className="space-y-1.5 mb-3">
                {linkedTasks.map(t => (
                  <Link key={t.id} to={`/tasks/all?task=${t.id}`} data-testid={`linked-task-${t.id}`} className="flex items-center gap-2 text-xs bg-accent/20 border border-border rounded-sm px-2 py-1.5 hover:border-primary/50">
                    <CheckCircle size={13} weight={t.status === "Done" ? "fill" : "regular"} className={t.status === "Done" ? "text-rag-green-fg" : "text-muted-foreground"} />
                    <span className={`truncate flex-1 ${t.status === "Done" ? "line-through text-muted-foreground" : ""}`}>{t.title}</span>
                    <span className="font-mono text-[10px] text-muted-foreground">{t.assignee_name || "Unassigned"}</span>
                    {t.due_date && <span className={`font-mono text-[10px] ${t.overdue ? "text-rag-red-fg" : "text-muted-foreground"}`}>{t.due_date}</span>}
                  </Link>
                ))}
              </div>
            ) : <div className="text-xs text-muted-foreground mb-3">No tasks linked to this ticket.</div>}
            <Button data-testid="create-task-from-ticket" size="sm" variant="outline" onClick={() => setNewTaskOpen(true)} className="rounded-sm h-8 w-full font-mono text-[10px] uppercase"><Plus size={13} className="mr-1" /> Create task from this ticket</Button>
          </Card>
          <NewTaskDialog open={newTaskOpen} onClose={() => setNewTaskOpen(false)} defaults={{ title: inc.title, linked_ticket_id: inc.number, assignee_id: inc.assignee_id || "" }}
            onCreated={() => api.get(`/tickets/${id}/tasks`).then(r => setLinkedTasks(r.data)).catch(() => {})} />

          {/* Attachments */}
          <Card className="p-4 border-border rounded-sm" data-testid="attachments-card">
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
              <Paperclip size={13} className="text-primary" /> Attachments
            </div>
            {attachments.length > 0 ? (
              <div className="space-y-2 mb-3">
                {attachments.map(a => (
                  <div key={a.id} data-testid={`attachment-${a.id}`} className="bg-accent/20 border border-border rounded-sm px-2 py-1.5">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="truncate flex-1">{a.filename}</span>
                      <span className="text-[10px] font-mono text-muted-foreground">{(a.size/1024).toFixed(0)}KB</span>
                      <button data-testid={`download-attachment-${a.id}`} onClick={()=>downloadAttachment(a)} className="text-muted-foreground hover:text-primary"><DownloadSimple size={13} /></button>
                    </div>
                    {(a.content_type || "").startsWith("image/") && <AttachmentImage att={a} />}
                  </div>
                ))}
              </div>
            ) : <div className="text-xs text-muted-foreground mb-3">No attachments.</div>}
            <label className="block">
              <input data-testid="attachment-input" type="file" onChange={uploadFile} className="hidden" />
              <span className={`flex items-center justify-center gap-2 text-[11px] font-mono uppercase tracking-wider py-2 rounded-sm border border-dashed border-border cursor-pointer hover:border-primary hover:text-primary transition-colors ${uploading ? 'opacity-50' : ''}`}>
                <Paperclip size={13} /> {uploading ? "Uploading…" : "Upload file (5MB max)"}
              </span>
            </label>
          </Card>
        </div>
      </div>

      <Dialog open={!!resolveDlg} onOpenChange={(o)=>{ if(!o) setResolveDlg(null); }}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="resolution-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-mono uppercase text-sm tracking-widest">
              Resolution summary
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Briefly describe how this ticket was {resolveDlg?.status === "closed" ? "closed" : "resolved"}. This is saved on the ticket and shown in its history.</p>
            <Textarea
              data-testid="resolution-note-input"
              value={resolveDlg?.note || ""}
              onChange={(e)=>setResolveDlg(d => ({ ...d, note: e.target.value }))}
              placeholder="e.g. Reset the user's VPN profile and confirmed connectivity."
              className="rounded-sm min-h-[110px] text-sm"
              autoFocus
            />
            <div className="text-[10px] font-mono text-muted-foreground text-right">{(resolveDlg?.note || "").trim().length}/10 min</div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm text-xs font-mono uppercase" onClick={()=>setResolveDlg(null)}>Cancel</Button>
            <Button data-testid="resolution-confirm-btn" className="rounded-sm text-xs font-mono uppercase" onClick={confirmResolve} disabled={(resolveDlg?.note || "").trim().length < 10}>
              {resolveDlg?.status === "closed" ? "Close ticket" : "Resolve ticket"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o)=>{ if(!o) setPreview(null); }}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="email-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-mono uppercase text-sm tracking-widest">
              <EnvelopeSimple size={16} className="text-primary" /> Email preview
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              <div className={`flex items-start gap-2 text-xs font-mono px-3 py-2 rounded-sm border ${preview.will_send ? 'border-rag-green/40 text-rag-green-fg bg-rag-green-bg' : 'border-rag-amber/40 text-rag-amber-fg bg-rag-amber-bg'}`} data-testid="preview-delivery-status">
                {preview.will_send ? <CheckCircle size={14} className="mt-0.5 shrink-0" /> : <WarningCircle size={14} className="mt-0.5 shrink-0" />}
                <span>{preview.will_send ? "This email will be delivered to the requester when you hit Post." : preview.reason}</span>
              </div>
              <div className="rounded-sm border border-border overflow-hidden">
                <div className="bg-accent/30 px-3 py-2 space-y-1 border-b border-border text-xs font-mono">
                  <div><span className="text-muted-foreground">To:&nbsp;</span><span data-testid="preview-to">{preview.to || "—"}</span></div>
                  {preview.cc?.length > 0 && <div><span className="text-muted-foreground">Cc:&nbsp;</span><span data-testid="preview-cc">{preview.cc.join(", ")}</span></div>}
                  <div><span className="text-muted-foreground">Subject:&nbsp;</span><span className="font-medium" data-testid="preview-subject">{preview.subject}</span></div>
                </div>
                <pre data-testid="preview-body" className="p-4 text-sm whitespace-pre-wrap font-sans leading-relaxed max-h-[320px] overflow-y-auto bg-background">{preview.body}</pre>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm text-xs font-mono uppercase" onClick={()=>setPreview(null)} data-testid="preview-close-btn">Close</Button>
            <Button className="rounded-sm text-xs font-mono uppercase" onClick={sendFromPreview} data-testid="preview-post-btn">
              <PaperPlaneRight size={14} className="mr-2" />Post reply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
